import React, { useState } from 'react';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stepper,
  Step,
  StepLabel,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  Avatar,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  SelectChangeEvent,
} from '@mui/material';
import {
  Add,
  Print,
  Radio,
  Work,
  Store,
  Flight,
  ShoppingCart,
  DesignServices,
  Calculate,
  Download,
  Visibility,
  Edit,
  LocalShipping,
  Schedule,
  CheckCircle,
  Warning,
  AttachMoney,
  Business,
} from '@mui/icons-material';
import { brandColors } from '../../theme';

interface MediaCategory {
  id: string;
  name: string;
  description: string;
  icon: React.ReactElement;
  color: string;
  available: boolean;
}

interface ServiceProvider {
  id: string;
  name: string;
  category: string;
  description: string;
  capabilities: string[];
  pricing: 'budget' | 'standard' | 'premium';
  deliveryTime: string;
  rating: number;
}

interface Order {
  id: number;
  title: string;
  category: string;
  provider: string;
  status: 'draft' | 'ordered' | 'production' | 'delivered' | 'completed';
  orderDate: string;
  deliveryDate: string;
  amount: number;
  quantity: number;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ p: 0 }}>{children}</Box>}
    </div>
  );
}

const MediaServices: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [newOrderOpen, setNewOrderOpen] = useState(false);
  const [designerOpen, setDesignerOpen] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedProvider, setSelectedProvider] = useState('');
  const [orderData, setOrderData] = useState({
    title: '',
    description: '',
    quantity: '',
    budget: '',
    deadline: '',
  });

  const steps = ['Kategorie wählen', 'Anbieter auswählen', 'Konfiguration', 'Bestellung'];

  const mediaCategories: MediaCategory[] = [
    {
      id: 'wm1',
      name: 'Print Medien (WM1)',
      description: 'Flyer, Broschüren, Plakate, Visitenkarten',
      icon: <Print />,
      color: brandColors.electricTeal,
      available: true
    },
    {
      id: 'wm2',
      name: 'Radio Werbung (WM2)',
      description: 'Radio Spots, Audio Produktion',
      icon: <Radio />,
      color: brandColors.electricTeal,
      available: true
    },
    {
      id: 'wm4',
      name: 'Job Portal (WM4)',
      description: 'Stellenanzeigen, Recruitment',
      icon: <Work />,
      color: brandColors.electricTeal,
      available: true
    },
    {
      id: 'wm5',
      name: 'Außenwerbung (WM5)',
      description: 'Außenwerbung, Verkehrsmittelwerbung',
      icon: <LocalShipping />,
      color: brandColors.electricTeal,
      available: true
    },
    {
      id: 'wm6',
      name: 'Trikots (WM6)',
      description: 'Teamwear, individuell bedruckte Trikots',
      icon: <ShoppingCart />,
      color: brandColors.electricTeal,
      available: true
    },
    {
      id: 'wm7',
      name: 'Zeitungsanzeigen (WM7)',
      description: 'Anzeigen in regionalen Zeitungen',
      icon: <Print />,
      color: brandColors.electricTeal,
      available: true
    },
    {
      id: 'wm8',
      name: 'Andere Werbemittel (WM8)',
      description: 'Sonstige Materialien und Giveaways',
      icon: <Store />,
      color: brandColors.electricTeal,
      available: true
    },
  ];

  const serviceProviders: ServiceProvider[] = [
    {
      id: 'flyeralarm',
      name: 'Flyeralarm',
      category: 'wm1',
      description: 'Führender Anbieter für Druckerzeugnisse und Werbemittel',
      capabilities: ['Flyer', 'Broschüren', 'Plakate', 'Visitenkarten', 'Banner'],
      pricing: 'budget',
      deliveryTime: '3-5 Werktage',
      rating: 4.5
    },
    {
      id: 'jobboerse',
      name: 'Jobbörse',
      category: 'wm4',
      description: 'Spezialisiert auf Stellenanzeigen und Recruitment',
      capabilities: ['Online Stellenanzeigen', 'Print Anzeigen', 'Social Media Recruiting'],
      pricing: 'standard',
      deliveryTime: '1-2 Werktage',
      rating: 4.2
    },
    {
      id: 'roadstore',
      name: 'Roadstore',
      category: 'wm5',
      description: 'Außenwerbung und mobile Werbedisplays',
      capabilities: ['Fahrzeugwerbung', 'Digitale Displays', 'Straßenwerbung'],
      pricing: 'premium',
      deliveryTime: '7-10 Werktage',
      rating: 4.7
    },
    {
      id: 'radio-partner',
      name: 'Radio Partner',
      category: 'wm2',
      description: 'Professionelle Radio Spot Produktion',
      capabilities: ['Spot Produktion', 'Voice Over', 'Musik & Sound'],
      pricing: 'standard',
      deliveryTime: '5-7 Werktage',
      rating: 4.3
    },
  ];

  const orders: Order[] = [
    {
      id: 1,
      title: 'Sommer Flyer Design',
      category: 'wm1',
      provider: 'Flyeralarm',
      status: 'production',
      orderDate: '2025-09-10',
      deliveryDate: '2025-09-17',
      amount: 1250,
      quantity: 5000
    },
    {
      id: 2,
      title: 'Handwerker Stellenanzeige',
      category: 'wm4',
      provider: 'Jobbörse',
      status: 'completed',
      orderDate: '2025-09-05',
      deliveryDate: '2025-09-07',
      amount: 850,
      quantity: 1
    },
    {
      id: 3,
      title: 'Fahrzeugbeschriftung',
      category: 'wm5',
      provider: 'Roadstore',
      status: 'ordered',
      orderDate: '2025-09-12',
      deliveryDate: '2025-09-25',
      amount: 3200,
      quantity: 3
    },
  ];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'var(--success)';
      case 'delivered': return 'var(--brand)';
      case 'production': return 'var(--warning)';
      case 'ordered': return 'var(--slate-600)';
      default: return 'var(--slate-600)';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'completed': return 'Abgeschlossen';
      case 'delivered': return 'Geliefert';
      case 'production': return 'In Produktion';
      case 'ordered': return 'Bestellt';
      default: return 'Entwurf';
    }
  };

  const getPricingColor = (pricing: string) => {
    switch (pricing) {
      case 'budget': return '#4caf50';
      case 'standard': return '#ff9800';
      case 'premium': return '#f44336';
      default: return '#9e9e9e';
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleNewOrderOpen = () => {
    setNewOrderOpen(true);
  };

  const handleNewOrderClose = () => {
    setNewOrderOpen(false);
    setActiveStep(0);
    setSelectedCategory('');
    setSelectedProvider('');
    setOrderData({
      title: '',
      description: '',
      quantity: '',
      budget: '',
      deadline: '',
    });
  };

  const handleDesignerOpen = () => {
    setDesignerOpen(true);
  };

  const handleDesignerClose = () => {
    setDesignerOpen(false);
  };

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleCategorySelect = (categoryId: string) => {
    setSelectedCategory(categoryId);
  };

  const handleProviderSelect = (providerId: string) => {
    setSelectedProvider(providerId);
  };

  const handleInputChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setOrderData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const handleSelectChange = (field: string) => (event: SelectChangeEvent) => {
    setOrderData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const getAvailableProviders = () => {
    return serviceProviders.filter(provider => provider.category === selectedCategory);
  };

  return (
    <Box>
      {/* Header Section (neutral) */}
      <Box sx={{ mb: 4, p: 3, borderRadius: 'var(--radius)', border: '1px solid var(--line)', backgroundColor: 'var(--card)' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h2" sx={{ fontWeight: 600, mb: 1 }}>
              Media Services
            </Typography>
            <Typography variant="body1" sx={{ color: 'var(--muted)' }}>
              Werbemedien bestellen und verwalten - Von Print bis Digital
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button variant="outlined" size="large" startIcon={<DesignServices />} onClick={handleDesignerOpen}>
              Designer Tool
            </Button>
            <Button variant="contained" size="large" startIcon={<Add />} onClick={handleNewOrderOpen}>
              Neue Bestellung
            </Button>
          </Box>
        </Box>
      </Box>
      {/* Quick Stats */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid
          size={{
            xs: 12,
            sm: 6,
            md: 3
          }}>
          <Card sx={{ border: '1px solid var(--line)', backgroundColor: 'var(--card)', borderRadius: 'var(--radius)' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 900, color: brandColors.midnightSteel }}>
                    {orders.length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Aktive Bestellungen
                  </Typography>
                </Box>
                <ShoppingCart sx={{ fontSize: 40, color: 'var(--brand)' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid
          size={{
            xs: 12,
            sm: 6,
            md: 3
          }}>
          <Card sx={{ border: '1px solid var(--line)', backgroundColor: 'var(--card)', borderRadius: 'var(--radius)' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 900, color: brandColors.midnightSteel }}>
                    {serviceProviders.length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Service Partner
                  </Typography>
                </Box>
                <Business sx={{ fontSize: 40, color: 'var(--brand)' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid
          size={{
            xs: 12,
            sm: 6,
            md: 3
          }}>
          <Card sx={{ border: '1px solid var(--line)', backgroundColor: 'var(--card)', borderRadius: 'var(--radius)' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 900, color: brandColors.midnightSteel }}>
                    €{orders.reduce((sum, order) => sum + order.amount, 0).toLocaleString()}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Gesamt Volumen
                  </Typography>
                </Box>
                <AttachMoney sx={{ fontSize: 40, color: 'var(--brand)' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid
          size={{
            xs: 12,
            sm: 6,
            md: 3
          }}>
          <Card sx={{ border: '1px solid var(--line)', backgroundColor: 'var(--card)', borderRadius: 'var(--radius)' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 900, color: brandColors.midnightSteel }}>
                    {mediaCategories.filter(cat => cat.available).length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Verfügbare Kategorien
                  </Typography>
                </Box>
                <Store sx={{ fontSize: 40, color: 'var(--brand)' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      {/* Main Content */}
      <Card>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={handleTabChange}>
            <Tab label="Kategorien" />
            <Tab label="Service Partner" />
            <Tab label="Bestellungen" />
            <Tab label="Budget Management" />
          </Tabs>
        </Box>

        <TabPanel value={tabValue} index={0}>
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, color: brandColors.midnightSteel }}>
              Werbemedien Kategorien
            </Typography>
            <Grid container spacing={3}>
              {mediaCategories.map((category) => (
                <Grid
                  key={category.id}
                  size={{
                    xs: 12,
                    sm: 6,
                    md: 4
                  }}>
                  <Card sx={{ 
                    cursor: category.available ? 'pointer' : 'not-allowed',
                    opacity: category.available ? 1 : 0.6,
                    border: `1px solid ${category.color}30`,
                    '&:hover': category.available ? { boxShadow: 4 } : {}
                  }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Avatar sx={{ bgcolor: category.color, mr: 2, width: 56, height: 56 }}>
                          {category.icon}
                        </Avatar>
                        <Box>
                          <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            {category.name}
                          </Typography>
                          <Chip 
                            label={category.available ? 'Verfügbar' : 'In Entwicklung'} 
                            size="small"
                            color={category.available ? 'success' : 'warning'}
                          />
                        </Box>
                      </Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {category.description}
                      </Typography>
                      {category.available && (
                        <Button 
                          variant="outlined" 
                          fullWidth
                          sx={{ 
                            borderColor: category.color,
                            color: category.color,
                            '&:hover': { bgcolor: `${category.color}10` }
                          }}
                        >
                          Konfigurieren
                        </Button>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, color: brandColors.midnightSteel }}>
              Service Partner & Anbieter
            </Typography>
            <Grid container spacing={3}>
              {serviceProviders.map((provider) => (
                <Grid
                  key={provider.id}
                  size={{
                    xs: 12,
                    md: 6
                  }}>
                  <Card>
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                        <Box>
                          <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            {provider.name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {provider.description}
                          </Typography>
                        </Box>
                        <Chip 
                          label={provider.pricing}
                          size="small"
                          sx={{ 
                            bgcolor: `${getPricingColor(provider.pricing)}20`,
                            color: getPricingColor(provider.pricing),
                            textTransform: 'capitalize'
                          }}
                        />
                      </Box>
                      
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="caption" color="text.secondary">Lieferzeit:</Typography>
                        <Typography variant="body2">{provider.deliveryTime}</Typography>
                      </Box>
                      
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="caption" color="text.secondary">Leistungen:</Typography>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                          {provider.capabilities.map((capability) => (
                            <Chip key={capability} label={capability} size="small" variant="outlined" />
                          ))}
                        </Box>
                      </Box>
                      
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" color="text.secondary">
                          ⭐ {provider.rating}/5.0
                        </Typography>
                        <Button 
                          variant="contained" 
                          size="small"
                          sx={{ bgcolor: brandColors.electricTeal }}
                        >
                          Beauftragen
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          <TableContainer component={Paper} elevation={0}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><Typography variant="subtitle2" fontWeight={600}>Bestellung</Typography></TableCell>
                  <TableCell><Typography variant="subtitle2" fontWeight={600}>Kategorie</Typography></TableCell>
                  <TableCell><Typography variant="subtitle2" fontWeight={600}>Anbieter</Typography></TableCell>
                  <TableCell><Typography variant="subtitle2" fontWeight={600}>Status</Typography></TableCell>
                  <TableCell><Typography variant="subtitle2" fontWeight={600}>Liefertermin</Typography></TableCell>
                  <TableCell><Typography variant="subtitle2" fontWeight={600}>Betrag</Typography></TableCell>
                  <TableCell><Typography variant="subtitle2" fontWeight={600}>Aktionen</Typography></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {orders.map((order) => (
                  <TableRow key={order.id} hover>
                    <TableCell>
                      <Typography variant="subtitle1" fontWeight={600}>{order.title}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Bestellt: {order.orderDate} • Menge: {order.quantity}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={order.category.toUpperCase()}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>{order.provider}</TableCell>
                    <TableCell>
                      <Chip 
                        label={getStatusText(order.status)} 
                        size="small"
                        sx={{ 
                          bgcolor: `${getStatusColor(order.status)}20`,
                          color: getStatusColor(order.status),
                          fontWeight: 600
                        }}
                      />
                    </TableCell>
                    <TableCell>{order.deliveryDate}</TableCell>
                    <TableCell>€{order.amount.toLocaleString()}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <IconButton size="small" sx={{ color: brandColors.electricTeal }}>
                          <Visibility />
                        </IconButton>
                        <IconButton size="small" sx={{ color: '#ff9800' }}>
                          <Edit />
                        </IconButton>
                        <IconButton size="small" sx={{ color: '#4caf50' }}>
                          <Download />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        <TabPanel value={tabValue} index={3}>
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 600, color: brandColors.midnightSteel }}>
              Budget Management (ähnlich Marcapo)
            </Typography>
            
            <Grid container spacing={3}>
              <Grid
                size={{
                  xs: 12,
                  md: 8
                }}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" sx={{ mb: 2 }}>Budget Übersicht</Typography>
                    <Box sx={{ 
                      height: 300, 
                      bgcolor: '#f9f9f9', 
                      borderRadius: 2, 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center' 
                    }}>
                      <Typography variant="h6" color="text.secondary">
                        📊 Budget Chart (Liste-basierte Logik)
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid
                size={{
                  xs: 12,
                  md: 4
                }}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" sx={{ mb: 2 }}>Budget Kategorien</Typography>
                    <List>
                      <ListItem>
                        <ListItemIcon>
                          <Calculate sx={{ color: brandColors.electricTeal }} />
                        </ListItemIcon>
                        <ListItemText
                          primary="Print Medien"
                          secondary="€15,000 / €20,000"
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <Calculate sx={{ color: brandColors.neonLime }} />
                        </ListItemIcon>
                        <ListItemText
                          primary="Digital Media"
                          secondary="€8,500 / €15,000"
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <Calculate sx={{ color: brandColors.midnightSteel }} />
                        </ListItemIcon>
                        <ListItemText
                          primary="Außenwerbung"
                          secondary="€3,200 / €10,000"
                        />
                      </ListItem>
                    </List>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        </TabPanel>
      </Card>
      {/* New Order Dialog */}
      <Dialog open={newOrderOpen} onClose={handleNewOrderClose} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ bgcolor: brandColors.electricTeal, color: brandColors.clearLight }}>
          Neue Bestellung erstellen
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <Box sx={{ p: 3 }}>
            <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>

            {activeStep === 0 && (
              <Box>
                <Typography variant="h6" sx={{ mb: 3 }}>Werbemedien Kategorie auswählen</Typography>
                <Grid container spacing={3}>
                  {mediaCategories.filter(cat => cat.available).map((category) => (
                    <Grid
                      key={category.id}
                      size={{
                        xs: 12,
                        sm: 6,
                        md: 4
                      }}>
                      <Card 
                        sx={{ 
                          cursor: 'pointer', 
                          border: selectedCategory === category.id ? `2px solid ${category.color}` : '1px solid #e0e0e0',
                          '&:hover': { boxShadow: 4 } 
                        }}
                        onClick={() => handleCategorySelect(category.id)}
                      >
                        <CardContent sx={{ textAlign: 'center', p: 3 }}>
                          <Avatar sx={{ 
                            bgcolor: selectedCategory === category.id ? category.color : '#f5f5f5',
                            width: 64,
                            height: 64,
                            mx: 'auto',
                            mb: 2
                          }}>
                            <Box sx={{ 
                              fontSize: 32, 
                              color: selectedCategory === category.id ? 'white' : category.color 
                            }}>
                              {category.icon}
                            </Box>
                          </Avatar>
                          <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            {category.name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {category.description}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}

            {activeStep === 1 && (
              <Box>
                <Typography variant="h6" sx={{ mb: 3 }}>Service Partner auswählen</Typography>
                <Grid container spacing={3}>
                  {getAvailableProviders().map((provider) => (
                    <Grid
                      key={provider.id}
                      size={{
                        xs: 12,
                        md: 6
                      }}>
                      <Card 
                        sx={{ 
                          cursor: 'pointer', 
                          border: selectedProvider === provider.id ? `2px solid ${brandColors.electricTeal}` : '1px solid #e0e0e0',
                          '&:hover': { boxShadow: 4 } 
                        }}
                        onClick={() => handleProviderSelect(provider.id)}
                      >
                        <CardContent>
                          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                            {provider.name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            {provider.description}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 2 }}>
                            {provider.capabilities.slice(0, 3).map((capability) => (
                              <Chip key={capability} label={capability} size="small" variant="outlined" />
                            ))}
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="body2">
                              {provider.deliveryTime}
                            </Typography>
                            <Chip 
                              label={provider.pricing}
                              size="small"
                              sx={{ 
                                bgcolor: `${getPricingColor(provider.pricing)}20`,
                                color: getPricingColor(provider.pricing),
                                textTransform: 'capitalize'
                              }}
                            />
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}

            {activeStep === 2 && (
              <Box>
                <Typography variant="h6" sx={{ mb: 3 }}>Bestellung konfigurieren</Typography>
                <Grid container spacing={3}>
                  <Grid size={12}>
                    <TextField
                      fullWidth
                      label="Projekt Titel"
                      value={orderData.title}
                      onChange={handleInputChange('title')}
                    />
                  </Grid>
                  <Grid size={12}>
                    <TextField
                      fullWidth
                      label="Beschreibung"
                      multiline
                      rows={4}
                      value={orderData.description}
                      onChange={handleInputChange('description')}
                    />
                  </Grid>
                  <Grid
                    size={{
                      xs: 12,
                      sm: 6
                    }}>
                    <TextField
                      fullWidth
                      label="Anzahl/Menge"
                      type="number"
                      value={orderData.quantity}
                      onChange={handleInputChange('quantity')}
                    />
                  </Grid>
                  <Grid
                    size={{
                      xs: 12,
                      sm: 6
                    }}>
                    <TextField
                      fullWidth
                      label="Budget (€)"
                      type="number"
                      value={orderData.budget}
                      onChange={handleInputChange('budget')}
                    />
                  </Grid>
                  <Grid size={12}>
                    <TextField
                      fullWidth
                      label="Gewünschter Liefertermin"
                      type="date"
                      value={orderData.deadline}
                      onChange={handleInputChange('deadline')}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                </Grid>
              </Box>
            )}

            {activeStep === 3 && (
              <Box>
                <Typography variant="h6" sx={{ mb: 3 }}>Bestellung bestätigen</Typography>
                <Paper sx={{ p: 3, bgcolor: '#f9f9f9' }}>
                  <Grid container spacing={2}>
                    <Grid size={6}>
                      <Typography variant="body2" color="text.secondary">Kategorie:</Typography>
                      <Typography variant="body1">{mediaCategories.find(cat => cat.id === selectedCategory)?.name}</Typography>
                    </Grid>
                    <Grid size={6}>
                      <Typography variant="body2" color="text.secondary">Anbieter:</Typography>
                      <Typography variant="body1">{serviceProviders.find(prov => prov.id === selectedProvider)?.name}</Typography>
                    </Grid>
                    <Grid size={6}>
                      <Typography variant="body2" color="text.secondary">Titel:</Typography>
                      <Typography variant="body1">{orderData.title}</Typography>
                    </Grid>
                    <Grid size={6}>
                      <Typography variant="body2" color="text.secondary">Budget:</Typography>
                      <Typography variant="body1">€{orderData.budget}</Typography>
                    </Grid>
                  </Grid>
                </Paper>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={handleNewOrderClose}>Abbrechen</Button>
          <Button disabled={activeStep === 0} onClick={handleBack}>
            Zurück
          </Button>
          <Button 
            variant="contained" 
            onClick={activeStep === steps.length - 1 ? handleNewOrderClose : handleNext}
            disabled={
              (activeStep === 0 && !selectedCategory) ||
              (activeStep === 1 && !selectedProvider)
            }
            sx={{ 
              bgcolor: brandColors.electricTeal,
              '&:hover': { bgcolor: brandColors.midnightSteel }
            }}
          >
            {activeStep === steps.length - 1 ? 'Bestellung aufgeben' : 'Weiter'}
          </Button>
        </DialogActions>
      </Dialog>
      {/* Designer Tool Dialog */}
      <Dialog open={designerOpen} onClose={handleDesignerClose} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ bgcolor: brandColors.electricTeal, color: brandColors.clearLight }}>
          Designer Tool (Marcapo-Style)
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Box sx={{ 
            height: 500, 
            bgcolor: '#f9f9f9', 
            borderRadius: 2, 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            border: '2px dashed #ccc'
          }}>
            <Box sx={{ textAlign: 'center' }}>
              <DesignServices sx={{ fontSize: 80, color: '#ccc', mb: 2 }} />
              <Typography variant="h5" color="text.secondary" sx={{ mb: 1 }}>
                Designer Tool Interface
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Hier würde das Marcapo-ähnliche Designer Tool für Werbemittel-Erstellung erscheinen
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={handleDesignerClose}>Schließen</Button>
          <Button variant="contained" sx={{ bgcolor: brandColors.electricTeal }}>
            Design speichern
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MediaServices;
